<script lang="ts">
    import {getLocalization} from 'src/core';
    import {AUTHORITY_LEADER_CODE, DA_AND_NUMBER_REGEX, DOCUMENT_LEADER_CODE, IND_1_CODE, IND_2_CODE, REFHOLDER_CODE, TOC_CODE, type ViewableField} from 'src/typings';
    import {FieldErrorPopover} from 'src/features/record/field-error';
    import {isFieldWithError} from 'shared/utils/record-utils';
    import {getRecordPath} from 'shared/utils/data-service-utils';
    import {exists} from 'shared/utils/custom-utils';
    import KpClassicTable from 'shared/ui-widgets/table/classic/KpClassicTable.svelte';

    export let fields: ViewableField[];

    const localize = getLocalization();

    function fieldCodeToViewableCode(code: string): string {
        switch (code) {
            case DOCUMENT_LEADER_CODE:
            case AUTHORITY_LEADER_CODE:
                return 'ldr';
            case IND_1_CODE:
                return 'i1';
            case IND_2_CODE:
                return 'i2';
            case REFHOLDER_CODE:
                return '➔';
        }
        if (DA_AND_NUMBER_REGEX.test(code)) {
            return code.substring(1);
        }
        return code;
    }
</script>

<KpClassicTable additionalClasses="marc-table"
                stripedRows
                hoverRows
                colorAccented
                columnHeadersCentered
                horizontallyDivided>

    <tr slot="header">
        <th class="header-field-number">{localize(/* @kp-localization commons.pole */ 'commons.pole')}</th>
        <th class="header-field-content">{localize(/* @kp-localization detail.obsahPole */ 'detail.obsahPole')}</th>
    </tr>

    <svelte:fragment slot="body">
        {#each fields as topField}
            {#if topField.code !== TOC_CODE}
                <tr class="field_{topField.code}" title={topField.fieldTypeText}>
                    <td class="field-code">{fieldCodeToViewableCode(topField.code)}</td>

                    {#if topField.fields.length === 0}
                        <td>
                            {#if topField.raw !== null}
                                <span class="field-value-text" title={topField.fieldTypeText}>{topField.raw}</span>
                            {/if}

                            {#if isFieldWithError(topField)}
                                <FieldErrorPopover error={topField.error}/>
                            {/if}
                        </td>
                    {:else}
                        <td class="obsahPole">
                            {#each topField.fields as subfield}
                                <span class="field_{subfield.code}" title={subfield.fieldTypeText}>
                                    <span class="subfield-code">{fieldCodeToViewableCode(subfield.code)}</span>

                                    {#if exists(subfield.originReference)}
                                        <a href={getRecordPath(subfield.originReference.id)} class="field-value-text">{subfield.text}</a>
                                    {:else if subfield.url}
                                        <a href={subfield.raw} target="_blank" class="field-value-text">{subfield.raw}</a>
                                    {:else if subfield.raw !== null}
                                        <span class="field-value-text">{subfield.raw}</span>
                                    {/if}

                                    {#if isFieldWithError(subfield)}
                                        <FieldErrorPopover error={subfield.error}/>
                                    {/if}
                                </span>
                            {/each}
                        </td>
                    {/if}
                </tr>
            {/if}
        {/each}
    </svelte:fragment>
</KpClassicTable>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    th {
        padding: @spacing-s @spacing-sm !important;
    }

    td {
        padding: @spacing-s @spacing-sm !important;
    }

    .subfield-code {
        color: gray;
        font-size: 0.8em;
    }

    .field-error-text {
        color: red;
        font-size: 0.8em;
    }

    :global(.marc-table) {
        table-layout: fixed;
        width: 100%;
        border-radius: @border-radius-default;
        overflow: hidden; // To show border radius

        tr > th,
        tr > td {
            &:first-child {
                text-align: center;
                white-space: nowrap;
            }
        }

        .header-field-content {
            width: 85%;
        }
    }
</style>
